<template>
    <div>
        <h2 class="headline">Läkemedelsöverkänslighet</h2>

        <form @submit.prevent id="alertInformation-details" autocomplete="off">
            <div class="form-group k3-form fullscreen">
                <input-wrapper
                    v-if="!fieldsDeactivated"
                    :disabled="false"
                    name="ascertainedDate"
                    v-model="form.ascertainedDate"
                    :colspan="8"
                    :component="widgets.DateWidget"
                    label="Fastställandedatum"
                />
                <input-wrapper
                    v-if="!fieldsDeactivated"
                    :disabled="false"
                    :component="widgets.DateWidget"
                    name="verifiedTime"
                    v-model="form.verifiedTime"
                    :colspan="8"
                    label="Verifierat datum"
                />
                <div class="colspan c8">
                    <h4>Giltighetstid</h4>
                </div>
                <input-wrapper
                    :disabled="false"
                    :component="widgets.DateWidget"
                    name="validityTimePeriodStart"
                    v-model="form.validityTimePeriodStart"
                    :required="true"
                    :colspan="3"
                    label="Från"
                />
                <input-wrapper
                    :disabled="false"
                    :component="widgets.DateWidget"
                    name="validityTimePeriodEnd"
                    :required="false"
                    v-model="form.validityTimePeriodEnd"
                    :colspan="3"
                    label="Till"
                />
                <input-wrapper
                    :disabled="false"
                    :component="widgets.TextAreaWidget"
                    name="comment"
                    v-model="form.comment"
                    :maxlength="140"
                    :required="true"
                    :colspan="8"
                    label="Kommentar"
                />
                <input-wrapper
                    :component="widgets.SelectWidget"
                    name="degreeOfSeverity"
                    v-model="form.degreeOfSeverity"
                    :required="true"
                    apisearch="/uppmarksamhetssignal/dos/"
                    :colspan="8"
                    label="Allvarlighetsgrad"
                    :displayCode="true"
                />
                <input-wrapper
                    :component="widgets.SelectWidget"
                    name="degreeOfCertainty"
                    v-model="form.degreeOfCertainty"
                    apisearch="/uppmarksamhetssignal/doc/"
                    :colspan="8"
                    label="Visshetsgrad"
                    :displayCode="true"
                />
                <input-wrapper
                    :component="widgets.SuggestWidget"
                    name="atcSubstance"
                    v-model="form.atcSubstance"
                    :required="false"
                    apisearch="/sil/atc/"
                    :colspan="8"
                    label="ATC-kod"
                    :displayCode="true"
                />
                <input-wrapper
                    v-if="!fieldsDeactivated"
                    :component="widgets.TextWidget"
                    name="noneATCSubstance"
                    v-model="form.noneATCSubstance"
                    :required="false"
                    :colspan="8"
                    label="Substans ej enligt ATC"
                />
                <input-wrapper
                    v-if="!fieldsDeactivated"
                    :component="widgets.TextAreaWidget"
                    name="noneATCSubstanceComment"
                    v-model="form.noneATCSubstanceComment"
                    :required="false"
                    :colspan="8"
                    label='Kommentar till "Substans ej enligt ATC"'
                />
                <input-wrapper
                    :component="widgets.ExpandableSuggest"
                    name="Läkemedel"
                    v-model="form.pharmaceuticalProduct"
                    apisearch="/sil/distributeddrugs/"
                    :map="
                        (el) => {
                            return {
                                id: el.nplId,
                                code: el.nplId,
                                displayName: el.displayName,
                            };
                        }
                    "
                    :colspan="8"
                    label="Läkemedel"
                />
            </div>
            <ButtonGroup>
                <Button type="button" @click="sign" size="primary">
                    <img src="@/assets/sign_white.svg" />Signera
                </Button>
                <Button @click="save" type="button" size="primary">
                    <img src="@/assets/send_white.svg" />Spara uppmärksamhetssignal
                </Button>
                <Button type="button" @click="cancel" variant="contour" size="secondary">Avbryt</Button>
            </ButtonGroup>
        </form>
    </div>
</template>

<script>
    import widgets from "@/components/widgets/inputs";
    import InputWrapper from "@/components/widgets/inputs/InputWrapper.vue";
    import linkEvents from "@/linkEvents";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import Button from "@/components/Button.vue";
    import { isEqual } from "lodash";
    import { removeEmptyOptions } from "@/tabs/AlertInformation/alertInformation";

    const createDefaultForm = () => ({
        ascertainedDate: null,
        verifiedTime: null,
        validityTimePeriodStart: new Date().toLocaleDateString("sv-SE"),
        validityTimePeriodEnd: null,
        comment: "",
        degreeOfSeverity: null,
        degreeOfCertainty: null,
        type: "phs",
        typeOfSensitivity: "PharmaceuticalHyperSensitivity",
        atcSubstance: "",
        noneATCSubstance: null,
        noneATCSubstanceComment: null,
        pharmaceuticalProduct: [],
    });

    export default {
        name: "PharmaceuticalHyperSensitivity",
        props: ["chosenComponent", "alertInformationForm"],
        components: {
            Button,
            ButtonGroup,
            InputWrapper,
        },
        data() {
            return {
                form: createDefaultForm(),
                widgets: widgets,
                fieldsDeactivated: true,
            };
        },
        computed: {
            hasUnsavedData() {
                return !isEqual(createDefaultForm(), this.form);
            },
        },
        watch: {
            hasUnsavedData(value) {
                this.$emit("hasUnsavedData", value);
            },
        },
        methods: {
            cancel() {
                this.$emit("cancelAlertForm");
            },
            save() {
                if (!this.validateForm("alertInformation-details")) {
                    return;
                }

                if (this.form.validityTimePeriodEnd === "") {
                    this.form.validityTimePeriodEnd = null;
                }
                const filteredForm = removeEmptyOptions(this.form, ["pharmaceuticalProduct"]);
                this.$emit("save", filteredForm);
            },
            sign() {
                if (!this.validateForm("alertInformation-details")) {
                    return;
                }
                if (this.form.validityTimePeriodEnd === "") {
                    this.form.validityTimePeriodEnd = null;
                }
                const filteredForm = removeEmptyOptions(this.form, ["pharmaceuticalProduct"]);
                this.$emit("sign", filteredForm);
            },
            validateForm(formName) {
                const form = document.getElementById(formName);
                if (form.checkValidity() === true) return true;
                else {
                    const list = form.querySelectorAll(":invalid");
                    for (let item of list) {
                        item.focus();
                        item.blur();
                    }
                    form.querySelector(":invalid").focus();
                }
                return false;
            },
        },
        created() {
            linkEvents.$on("pharmaceuticalProduct_changed", this.update);
        },
    };
</script>

<style lang="sass" scoped>
    #receiver-details
        position: relative
        min-height: 600px
        min-width: 600px
        display: inline-block

    h2
        margin: 10px 0px 10px 0px !important

    .radiobutton-line
        display: inline-block
        margin-top: 12px
        margin-bottom: 30px

        label:not(:first-of-type)
            margin-left: 32px

    .btn-group
        float: none !important

    .headline
        margin-bottom: 20px !important
</style>
